import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ActiveNexusFilter,
  GanttChartData,
  GanttTabBarTab,
  PondooStatus,
  TaskDependency,
  WorkTask,
} from "../../types/ganttChart";
import {
  getGanttChartData,
  getPondooStatus,
  initiateGanttChartData,
  initiateNexusLandingDataExtraction,
  initiateOptimization,
} from "./action";
import labels from "../../utils/labels";
import { setSelectedNexusFilterDate } from "../nexusFilters";
import { reorderEmployeesInPlace } from "./employeeOrderingUtils";
import TeamColorGroupUtils, {
  TeamGroupColorMaps,
} from "../../utils/TeamColorGroupUtils";

type GanttChartTabAction = {
  payload: GanttTabBarTab;
};

type PendingDataFilters = { filter: string; date: string; requestId?: string };

export interface GanttChartSlice {
  selectedGanttChartTab: GanttTabBarTab;
  verticalColumnWidth: number;
  unplannedColumnWidth: number;
  verticalSvgWidth: number;
  selectedActiveNexusFilter?: ActiveNexusFilter;
  translateXValueInPercent: number;
  ganttChartData?: GanttChartData;
  pondooStatus?: PondooStatus;
  pondooStatusError?: string;
  showSpinner: boolean;
  error?: string;
  pendingData: PendingDataFilters[];
  clickedTaskDependencies?: TaskDependency[];
  scenarioIdToInitiatePondooSolution?: number;
  teamGroupColorMaps?: TeamGroupColorMaps;
  showSpinnerOnCustomData: boolean;
  currentRequestId?: string;
  previouslyLockedRouteShiftIds: number[];
  isContingencyMode: boolean;
  isContingencyPlanPending: boolean;
  isLockVisitsMode: boolean;
  hasLockVisitsChanged: boolean;
  refetchToken: number;
}

const initialState: GanttChartSlice = {
  selectedGanttChartTab: "Original",
  verticalColumnWidth: 0,
  unplannedColumnWidth: 0,
  verticalSvgWidth: 0,
  translateXValueInPercent: 0,
  showSpinner: false,
  pendingData: [],
  showSpinnerOnCustomData: false,
  previouslyLockedRouteShiftIds: [],
  isContingencyMode: false,
  isContingencyPlanPending: false,
  isLockVisitsMode: false,
  hasLockVisitsChanged: false,
  refetchToken: 0,
};

const ganttChartSSlice = createSlice({
  name: "gantt-chart",
  initialState,
  reducers: {
    setSelectedGanttChartTab(state, action: GanttChartTabAction) {
      state.selectedGanttChartTab = action.payload;
      state.isContingencyMode = false;
      state.isLockVisitsMode = false;
    },
    setIsContingencyMode(state, action: PayloadAction<boolean>) {
      state.isContingencyMode = action.payload;
    },
    setIsContingencyPlanPending(state, action: PayloadAction<boolean>) {
      state.isContingencyPlanPending = action.payload;
    },
    setIsLockVisitsMode(state, action: PayloadAction<boolean>) {
      state.isLockVisitsMode = action.payload;
      state.hasLockVisitsChanged = false;
    },
    setVerticalColumnWidth(state, action) {
      state.verticalColumnWidth = action.payload;
    },
    setUnplannedColumnWidth(state, action) {
      state.unplannedColumnWidth = action.payload;
    },
    setVerticalSvgWidth(state, action) {
      state.verticalSvgWidth = action.payload;
    },
    clearGanttChartData(state) {
      delete state.ganttChartData;
    },
    triggerRefetch(state) {
      delete state.ganttChartData;
      state.refetchToken++;
    },
    setShowSpinner(state) {
      state.showSpinner = true;
    },
    setHideSpinner(state) {
      state.showSpinner = false;
    },
    setClickedTaskDependencies(state, action) {
      state.clickedTaskDependencies = action.payload;
    },
    deleteClickedTaskDependencies(state) {
      delete state.clickedTaskDependencies;
    },
    resetGanttChartErrorMessage(state) {
      delete state.error;
    },
    setGanttChartErrorMessage(state, action: PayloadAction<string>) {
      state.error = action.payload;
    },
    clearPondooStatusData(state) {
      state.pondooStatusError = labels.pondooMissing;
    },

    updateLockedForPlanning(state, action) {
      // Updates the lockedForPlanning property on the employee object in the ganttChartData, without reloading everything from the server.
      const { idShift, lockedForPlanning } = action.payload;

      if (state.ganttChartData?.employees) {
        const employee = state.ganttChartData.employees.find(
          (employee) => employee.shiftSchedule.idShift === idShift,
        );
        if (employee) {
          employee.shiftSchedule.lockedForPlanning = lockedForPlanning;
        }
      }
    },
    updateWorkTaskLockedForPlanning(
      state,
      action: PayloadAction<{
        idTask: number;
        lockedForPlanning: boolean;
        isRollback?: boolean;
      }>,
    ) {
      const { idTask, lockedForPlanning, isRollback } = action.payload;
      state.ganttChartData?.employees.forEach((employee) => {
        const task = employee.tasks.workTasks.find(
          (t) => t.idTask === idTask,
        );
        if (task) {
          task.lockedForPlanning = lockedForPlanning;
          if (!isRollback) state.hasLockVisitsChanged = true;
        }
      });
    },
    setAllRoutesAsLockedForPlanning(state) {
      const employees = state.ganttChartData?.employees;
      if (!employees) return;

      const lockedShiftIds: number[] = [];

      //Locate all currently locked routes for storing, before updating to all locked.
      employees.forEach((employee) => {
        const shiftSchedule = employee.shiftSchedule;
        if (!shiftSchedule) return;

        if (shiftSchedule.lockedForPlanning) {
          lockedShiftIds.push(shiftSchedule.idShift);
        }

        shiftSchedule.lockedForPlanning = true;
      });

      state.previouslyLockedRouteShiftIds = lockedShiftIds;
    },
    undoLockAllRoutes(state) {
      const employees = state.ganttChartData?.employees;
      if (!employees) return;

      const lockedShiftIds = state.previouslyLockedRouteShiftIds;

      employees.forEach((employee) => {
        const shiftSchedule = employee.shiftSchedule;
        if (!shiftSchedule) return;

        shiftSchedule.lockedForPlanning = lockedShiftIds.includes(
          shiftSchedule.idShift,
        );
      });

      state.previouslyLockedRouteShiftIds = [];
    },
    // Updates lockedForPlanning on a single WorkTask after a successful /lock-task call, without reloading all gantt data.
    toggleTaskLockedForPlanning(state, action: PayloadAction<number>) {
      const idTask = action.payload;
      const employees = state.ganttChartData?.employees;
      if (!employees) return;

      for (const employee of employees) {
        const task = employee.tasks.workTasks.find((t: WorkTask) => t.idTask === idTask);
        if (task) {
          task.lockedForPlanning = !task.lockedForPlanning;
          return;
        }
      }
    },
    setGanttChartData(state, action: PayloadAction<GanttChartData>) {
      state.ganttChartData = action.payload;
    },
    updateEmployeeWorkTime(
      state,
      action: PayloadAction<{
        shiftId: number;
        shiftStartTime: string;
        shiftEndTime: string;
      }>,
    ) {
      const { shiftId, shiftStartTime, shiftEndTime } = action.payload;
      const employee = state.ganttChartData?.employees.find(
        (e) => e.shiftSchedule.idShift === shiftId,
      );
      if (employee) {
        employee.shiftStartTime = shiftStartTime;
        employee.shiftEndTime = shiftEndTime;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(initiateGanttChartData.pending, (state, action) => {
      delete state.ganttChartData;
      delete state.scenarioIdToInitiatePondooSolution;
      const filter = new URL(window.location.href).searchParams.get("filter");
      const date = new URL(window.location.href).searchParams.get("date");
      state.pendingData.push({
        filter: filter!,
        date: date!,
        requestId: action.meta.requestId,
      });
    });
    builder.addCase(initiateGanttChartData.fulfilled, (state, action) => {
      state.pendingData = state.pendingData.filter(
        (pendingDataObj) => pendingDataObj.requestId !== action.meta.requestId,
      );
      state.scenarioIdToInitiatePondooSolution = action.payload.idScenario;
    });
    builder.addCase(initiateGanttChartData.rejected, (state, action) => {
      // Remove spinner for requestID if request fails
      state.pendingData = state.pendingData.filter(
        (pendingDataObj) => pendingDataObj.requestId !== action.meta.requestId,
      );
    });
    builder.addCase(
      initiateNexusLandingDataExtraction.pending,
      (state, action) => {
        delete state.ganttChartData;
        delete state.scenarioIdToInitiatePondooSolution;
        const filter = new URL(window.location.href).searchParams.get("filter");
        const date = new URL(window.location.href).searchParams.get("date");
        state.pendingData.push({
          filter: filter!,
          date: date!,
          requestId: action.meta.requestId,
        });
      },
    );
    builder.addCase(
      initiateNexusLandingDataExtraction.fulfilled,
      (state, action) => {
        // Remove spinner when data is ready
        state.pendingData = state.pendingData.filter(
          (pendingDataObj) =>
            pendingDataObj.requestId !== action.meta.requestId,
        );
        state.scenarioIdToInitiatePondooSolution = action.payload.idScenario;
      },
    );
    builder.addCase(
      initiateNexusLandingDataExtraction.rejected,
      (state, action) => {
        // Remove spinner for requestID if request fails
        state.pendingData = state.pendingData.filter(
          (pendingDataObj) =>
            pendingDataObj.requestId !== action.meta.requestId,
        );
        state.showSpinner = false;
        state.error = labels.nexusConnectionError;
      },
    );
    builder.addCase(getGanttChartData.fulfilled, (state, action) => {
      state.showSpinner = false;
      state.isContingencyPlanPending = false;

      /*
       Rapidly switching between ganttChart tabs will cause the data to become out of sync. 
       To fix this, we ensure that data is only updated when the solutionsType matches the selected ganttChartTab.*/
      const tab =
        state.selectedGanttChartTab === "Optimised" ? "Pondoo" : "Nexus";

      if (state.ganttChartData && action.payload.solutionType !== tab) return;
      const ganttChart = reorderEmployeesInPlace(action.payload!);
      state.ganttChartData = ganttChart;

      if (ganttChart.useColorGroupingsAsTeamSplitter) {
        state.teamGroupColorMaps =
          TeamColorGroupUtils.computeTeamColorGroupMaps(ganttChart.employees);
      }
    });
    builder.addCase(getGanttChartData.pending, (state, action) => {
      state.currentRequestId = action.meta.requestId; // track latest request to avoid toggling spinner when old request is rejected, but new is pending.
      delete state.ganttChartData;
      delete state.teamGroupColorMaps;
      delete state.error;
      state.showSpinner = true;
    });
    builder.addCase(getGanttChartData.rejected, (state, action) => {
      if (state.currentRequestId !== action.meta.requestId) return;

      state.showSpinner = false;
      state.currentRequestId = undefined;
      const errorMessage = action.error.message;
      if (errorMessage?.includes("404")) {
        // state.error =
        //   "Noget gik galt, da vi forsøgte at hente din plan. Prøv igen.";
      } else {
        state.error = labels.fetchPlanError;
      }
    });
    builder.addCase(getPondooStatus.pending, (state) => {
      delete state.pondooStatusError;
      delete state.pondooStatus;
    });
    builder.addCase(getPondooStatus.fulfilled, (state, action) => {
      state.pondooStatus = action.payload;
    });
    builder.addCase(getPondooStatus.rejected, (state, action) => {
      state.pondooStatusError = labels.pondooStatusError;
      // Todo handle error on status bar.
    });
    builder.addCase(initiateOptimization.pending, (state) => {});
    builder.addCase(initiateOptimization.rejected, (state) => {
      // Todo handle error on status bar.
    });
    builder.addCase(initiateOptimization.fulfilled, (state, action) => {
      console.log(action.payload);
    });
    builder.addCase(setSelectedNexusFilterDate, (state) => {
      state.isLockVisitsMode = false;
    });
  },
});

export const {
  setSelectedGanttChartTab,
  setVerticalColumnWidth,
  setUnplannedColumnWidth,
  setVerticalSvgWidth,
  clearGanttChartData,
  setShowSpinner,
  setHideSpinner,
  setClickedTaskDependencies,
  deleteClickedTaskDependencies,
  setGanttChartErrorMessage,
  resetGanttChartErrorMessage,
  clearPondooStatusData,
  updateLockedForPlanning,
  toggleTaskLockedForPlanning,
  setGanttChartData,
  setAllRoutesAsLockedForPlanning,
  undoLockAllRoutes,
  updateEmployeeWorkTime,
  setIsContingencyMode,
  setIsContingencyPlanPending,
  setIsLockVisitsMode,
  updateWorkTaskLockedForPlanning,
  triggerRefetch,
} = ganttChartSSlice.actions;

export default ganttChartSSlice.reducer;
